import React, { useState, useEffect } from 'react';
import { X, TrendingUp, TrendingDown, Activity, Calendar, DollarSign, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getLocalApiUrl, authFetch } from '../config/api';

const TransactionGamingProfile = ({ memberId, memberName, onClose }) => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [sessions, setSessions] = useState([]);
  const [dailySummaries, setDailySummaries] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    loadData();
  }, [memberId, dateRange]);

  const loadData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const sessionsQuery = new URLSearchParams({
        member_id: memberId,
        ...(dateRange.from && { from: dateRange.from }),
        ...(dateRange.to && { to: dateRange.to }),
        limit: '100'
      });

      const sessionsRes = await authFetch(getLocalApiUrl(`/api/gaming/sessions?${sessionsQuery}`));
      const sessionsData = await sessionsRes.json().catch(() => ({}));

      const summaryQuery = new URLSearchParams({
        member_id: memberId,
        ...(dateRange.from && { from: dateRange.from }),
        ...(dateRange.to && { to: dateRange.to })
      });

      const summaryRes = await authFetch(getLocalApiUrl(`/api/gaming/combined/summary?${summaryQuery}`));
      const summaryData = await summaryRes.json().catch(() => ({}));

      const sessionsOk = sessionsRes.ok && sessionsData.success;
      const summaryOk = summaryRes.ok && summaryData.success;
      const gamingSessions = sessionsOk ? (sessionsData.data || []) : [];
      const dailyData = summaryOk ? (summaryData.data || []) : [];

      setSessions(gamingSessions);
      setDailySummaries(dailyData);

      if (gamingSessions.length > 0 || dailyData.length > 0) {
        calculateAnalytics(gamingSessions, dailyData);
        if (!sessionsOk || !summaryOk) {
          setLoadError('Some gaming endpoints failed; analytics use available data only.');
        } else {
          setLoadError(null);
        }
      } else {
        setAnalytics(null);
        if (!sessionsOk && !summaryOk) {
          setLoadError(sessionsData.error || summaryData.error || `Could not load gaming data (${sessionsRes.status})`);
        } else {
          setLoadError(null);
        }
      }
    } catch (error) {
      console.error('Error loading gaming data:', error);
      setLoadError(error?.message || 'Failed to load gaming data');
      setSessions([]);
      setDailySummaries([]);
      setAnalytics(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (sessions, dailyData) => {
    const safe = (n) => (typeof n === 'number' && !isNaN(n) && isFinite(n)) ? n : 0;
    // If we have daily data, use it; otherwise calculate from sessions
    let totalSpend = 0;
    let totalWins = 0;
    let totalVisits = 0;
    let highestDaySpend = 0;
    let visitsPerWeek = 0;
    let visitsPerMonth = 0;
    
    if (dailyData.length > 0) {
      // Use daily summary data (coerce to number to avoid string concatenation)
      totalSpend = dailyData.reduce((sum, d) => sum + Number(d.Turnover_alloc || 0), 0);
      totalWins = dailyData.reduce((sum, d) => sum + Number(d.CreditWin_alloc || 0), 0);
      totalVisits = dailyData.length;
      
      const highestDay = dailyData.reduce((max, d) => 
        Number(d.Turnover_alloc || 0) > Number(max?.Turnover_alloc || 0) ? d : max
      , dailyData[0]);
      highestDaySpend = Number(highestDay?.Turnover_alloc || 0);
      
      const firstVisit = dailyData[0]?.TradingDay ? new Date(dailyData[0].TradingDay) : new Date();
      const lastVisit = dailyData[dailyData.length - 1]?.TradingDay ? new Date(dailyData[dailyData.length - 1].TradingDay) : new Date();
      const daysDiff = Math.max(1, Math.ceil((lastVisit.getTime() - firstVisit.getTime()) / (1000 * 60 * 60 * 24)));
      visitsPerWeek = (totalVisits / daysDiff) * 7;
      visitsPerMonth = (totalVisits / daysDiff) * 30;
    } else if (sessions.length > 0) {
      // Calculate from sessions data (coerce to number to avoid string concatenation/NaN)
      totalSpend = sessions.reduce((sum, s) => sum + Number(s.Turnover ?? s.turnover ?? 0), 0);
      totalWins = sessions.reduce((sum, s) => sum + Number(s.CreditWin ?? s.creditwin ?? 0), 0);
      
      // Group by gaming date to count unique days/visits
      const dayMap = sessions.reduce((acc, s) => {
        const date = s.GamingDate || s.gamingdate || s.StartTime?.split('T')[0] || '';
        if (date) {
          if (!acc[date]) acc[date] = { turnover: 0, wins: 0 };
          acc[date].turnover += Number(s.Turnover ?? s.turnover ?? 0);
          acc[date].wins += Number(s.CreditWin ?? s.creditwin ?? 0);
        }
        return acc;
      }, {});
      
      const uniqueDays = Object.keys(dayMap);
      totalVisits = uniqueDays.length;
      highestDaySpend = Math.max(...Object.values(dayMap).map(d => d.turnover), 0);
      
      // Calculate date range from sessions
      const dates = sessions.map(s => new Date(s.StartTime || s.GamingDate || '')).filter(d => !isNaN(d.getTime()));
      if (dates.length > 1) {
        dates.sort((a, b) => a.getTime() - b.getTime());
        const firstVisit = dates[0];
        const lastVisit = dates[dates.length - 1];
        const daysDiff = Math.max(1, Math.ceil((lastVisit.getTime() - firstVisit.getTime()) / (1000 * 60 * 60 * 24)));
        visitsPerWeek = (totalVisits / daysDiff) * 7;
        visitsPerMonth = (totalVisits / daysDiff) * 30;
      }
    }
    
    const netLoss = totalSpend - totalWins;
    const avgSpendPerVisit = totalVisits > 0 ? totalSpend / totalVisits : 0;
    
    // Favorite venue and EGMs
    const venueCounts = sessions.reduce((acc, s) => {
      if (s.Venue) acc[s.Venue] = (acc[s.Venue] || 0) + 1;
      return acc;
    }, {});
    const favoriteVenue = Object.keys(venueCounts).length > 0 
      ? Object.keys(venueCounts).reduce((a, b) => venueCounts[a] > venueCounts[b] ? a : b, 'N/A')
      : 'N/A';
    
    // Get top 3 EGM IDs by frequency
    const egmCounts = sessions.reduce((acc, s) => {
      const egmId = s.EgmPK || 'Unknown';
      acc[egmId] = (acc[egmId] || 0) + 1;
      return acc;
    }, {});
    const favoriteEGMs = Object.entries(egmCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([egm]) => egm)
      .filter(egm => egm !== 'Unknown');
    
    // Average session duration
    const sessionsWithDuration = sessions.filter(s => s.StartTime && s.EndTime);
    const avgDuration = sessionsWithDuration.length > 0
      ? sessionsWithDuration.reduce((sum, s) => {
          const start = new Date(s.StartTime).getTime();
          const end = new Date(s.EndTime).getTime();
          return sum + (end - start) / (1000 * 60); // minutes
        }, 0) / sessionsWithDuration.length
      : 0;
    
    // Risk profiling (use safe values to avoid NaN in comparisons)
    const { riskProfile, riskFactors } = calculateRiskProfile(safe(avgSpendPerVisit), safe(visitsPerWeek), safe(netLoss), safe(totalSpend));
    
    setAnalytics({
      totalVisits,
      totalSpend: Math.round(safe(totalSpend)),
      totalWins: Math.round(safe(totalWins)),
      netLoss: Math.round(safe(netLoss)),
      avgSpendPerVisit: Math.round(safe(avgSpendPerVisit)),
      highestDaySpend: Math.round(safe(highestDaySpend)),
      visitsPerWeek: Math.round(safe(visitsPerWeek) * 10) / 10,
      visitsPerMonth: Math.round(safe(visitsPerMonth) * 10) / 10,
      favoriteVenue,
      favoriteEGMs: favoriteEGMs.length > 0 ? favoriteEGMs : ['N/A'],
      avgSessionDuration: Math.round(safe(avgDuration)),
      riskProfile,
      riskFactors
    });
  };

  const calculateRiskProfile = (avgSpend, visitsPerWeek, netLoss, totalSpend) => {
    const factors = [];
    let score = 0;
    
    // High frequency visits
    if (visitsPerWeek > 5) {
      score += 2;
      factors.push('High visit frequency (>5 per week)');
    } else if (visitsPerWeek > 3) {
      score += 1;
      factors.push('Moderate visit frequency');
    }
    
    // High spend per visit
    if (avgSpend > 5000) {
      score += 3;
      factors.push('Very high average spend per visit ($5000+)');
    } else if (avgSpend > 2000) {
      score += 2;
      factors.push('High average spend per visit ($2000+)');
    } else if (avgSpend > 1000) {
      score += 1;
      factors.push('Moderate spend per visit');
    }
    
    // Total spend threshold
    if (totalSpend > 12000) {
      score += 2;
      factors.push('Very high total spend ($12k+)');
    } else if (totalSpend > 5000) {
      score += 1;
      factors.push('High total spend ($5k+)');
    }
    
    // Net loss pattern
    if (netLoss > 10000) {
      score += 2;
      factors.push('Significant net losses ($10k+)');
    } else if (netLoss > 5000) {
      score += 1;
      factors.push('Moderate net losses ($5k+)');
    }
    
    // Determine risk profile
    let riskProfile;
    if (score >= 7) {
      riskProfile = 'CRITICAL';
    } else if (score >= 5) {
      riskProfile = 'HIGH';
    } else if (score >= 3) {
      riskProfile = 'MEDIUM';
    } else {
      riskProfile = 'LOW';
    }
    
    if (factors.length === 0) {
      factors.push('Normal gaming pattern');
    }
    
    return { riskProfile, riskFactors: factors };
  };

  const getRiskBadgeClass = (risk) => {
    switch (risk) {
      case 'CRITICAL': return 'bg-red-600 text-white hover:bg-red-700';
      case 'HIGH': return 'bg-red-100 text-red-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    const n = Number(amount);
    if (isNaN(n) || !isFinite(n)) return '$0';
    return `$${n.toLocaleString()}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-psg-teal to-teal-600 text-white">
          <div>
            <h2 className="text-2xl font-bold">Transaction & Gaming Profile</h2>
            <p className="text-sm opacity-90">{memberName} (ID: {memberId})</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Date Range Filter */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="input-field text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="input-field text-sm"
              />
            </div>
            <Button size="sm" onClick={loadData}>Apply</Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-3 font-medium border-b-2 transition ${
                activeTab === 'analytics'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Activity className="inline h-4 w-4 mr-2" />
              Behavioral Analytics
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`px-4 py-3 font-medium border-b-2 transition ${
                activeTab === 'sessions'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Calendar className="inline h-4 w-4 mr-2" />
              Gaming Sessions ({sessions.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading gaming data...</div>
            </div>
          ) : activeTab === 'analytics' ? (
            <div className="space-y-6">
              {loadError && (
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm">
                  {loadError}
                </div>
              )}
              {!analytics && !loadError && (
                <div className="rounded-md border border-slate-200 bg-slate-50 text-slate-600 px-4 py-8 text-center text-sm">
                  No gaming sessions or daily summaries for this member in the selected range. Behavioral analytics appear when data is available from the API.
                </div>
              )}
              {/* Risk Profile Card */}
              {analytics && (
                <Card className="border-2 border-gray-200">
                  <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <CardTitle className="flex items-center justify-between">
                      <span>Risk Profile Assessment</span>
                      <Badge className={getRiskBadgeClass(analytics.riskProfile)}>
                        {analytics.riskProfile}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-3">Risk Factors:</p>
                      {analytics.riskFactors.map((factor, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{factor}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Analytics Grid */}
              {analytics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Visit Patterns */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Visit Frequency</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Per Week:</span>
                          <span className="font-semibold">{analytics.visitsPerWeek}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Per Month:</span>
                          <span className="font-semibold">{analytics.visitsPerMonth}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Total Visits:</span>
                          <span className="font-semibold">{analytics.totalVisits}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Spend Patterns */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Spend Patterns</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Avg/Visit:</span>
                          <span className="font-semibold text-blue-600">{formatCurrency(analytics.avgSpendPerVisit)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Highest Day:</span>
                          <span className="font-semibold text-purple-600">{formatCurrency(analytics.highestDaySpend)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Total Spend:</span>
                          <span className="font-semibold">{formatCurrency(analytics.totalSpend)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Win/Loss */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Win/Loss Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Total Wins:</span>
                          <span className="font-semibold text-green-600 flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            {formatCurrency(analytics.totalWins)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Net Loss:</span>
                          <span className="font-semibold text-red-600 flex items-center gap-1">
                            <TrendingDown className="h-4 w-4" />
                            {formatCurrency(analytics.netLoss)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Return Rate:</span>
                          <span className="font-semibold">{analytics.totalSpend > 0 ? Math.round((analytics.totalWins / analytics.totalSpend) * 100) : 0}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Preferences */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Gaming Preferences</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs text-gray-500">Favorite Venue:</span>
                          <p className="font-semibold text-sm">{analytics.favoriteVenue}</p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Top EGM IDs:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {analytics.favoriteEGMs.map((egm, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-mono">
                                {egm}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Session Duration */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Session Behavior</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Avg Duration:</span>
                          <span className="font-semibold">{analytics.avgSessionDuration} min</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Sessions:</span>
                          <span className="font-semibold">{analytics.totalVisits}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          ) : (
            // Sessions Tab
            <div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Venue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">EGM ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Turnover</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Win</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sessions.length > 0 ? sessions.map((session, idx) => {
                      const duration = session.StartTime && session.EndTime
                        ? Math.round((new Date(session.EndTime).getTime() - new Date(session.StartTime).getTime()) / (1000 * 60))
                        : 0;
                      
                      return (
                        <tr key={session.SessionPK || idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            {session.StartTime ? new Date(session.StartTime).toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-3">{session.Venue || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs font-mono">
                              {session.EgmPK || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">{duration} min</td>
                          <td className="px-4 py-3 font-mono">${session.Turnover?.toLocaleString() || 0}</td>
                          <td className="px-4 py-3 font-mono text-green-600">${session.CreditWin?.toLocaleString() || 0}</td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                          No gaming sessions found for this member
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionGamingProfile;

